/**
 * Clasificadores de errores esperados (validación de negocio, permiso, red
 * transitoria). Módulo hoja sin imports para que `sentryUser`, `sentryOps`,
 * `sentryBeforeSend` y cualquier caller lo reusan sin require cycles.
 */

const EXPECTED_ERROR_NAMES = new Set([
  'StockReceiveValidationError',
  'StockInsufficientError',
  'StockTransferValidationError',
  'StockProductNotFoundError',
  'NonInventoriableProductError',
  'OfflineWriteBlockedError',
  'DatabaseUnavailableError',
  'CashSessionRequiredError',
  'CatalogSkuDuplicateError',
  'CatalogBarcodeDuplicateError',
  'SupplierCodeDuplicateError',
  'StockTransferNotFoundError',
  'PurchaseNotFoundError',
]);

/**
 * Backend error codes that represent expected business validation, not bugs.
 * Defense in depth: even if the client-side error mapping changes, the raw
 * ingestError from the backend is checked against these codes so expected
 * validation never reaches Sentry as a crash.
 */
const EXPECTED_INGEST_CODES = new Set([
  'stock_policy_block',
  'sale_already_voided',
  'customer_credit_limit_exceeded',
  'customer_blocked',
  'sale_draft_expired',
  'sale_draft_not_active',
  'cash_session_already_open',
]);

const EXPECTED_STOCK_DOMAIN_CODES = new Set([
  'STOCK_INSUFFICIENT',
  'STOCK_TRANSFER_VALIDATION',
  'STOCK_RECEIVE_VALIDATION',
  'STOCK_PRODUCT_NOT_FOUND',
  'NON_INVENTORIABLE_PRODUCT',
  'STOCK_TRANSFER_NOT_FOUND',
]);

/**
 * Códigos crudos del backend (RPC raise / PostgrestError.message) que son
 * validación de negocio o permiso esperado — no bug. Los repos los preservan
 * en `error.backendCode` (regla error-mapping: el código interno no se
 * muestra al usuario pero sí sirve para clasificar). Se matchean con
 * `includes` porque el mensaje crudo puede traer contexto extra.
 * `branchid_required` / `operationid_required` NO van acá: son bug del cliente.
 */
const EXPECTED_BACKEND_CODES = [
  'last_active_branch',
  'open_cash_session',
  'cash_session_already_open',
  'caja abierta',
  'branch_not_found',
  'branch_not_inactive',
  'branch limit reached',
  'multi_branch not enabled',
  'owner required',
  'module_permission_denied',
  'branch_access_denied',
];

function matchesExpectedBackendCode(raw: string): boolean {
  const code = raw.trim().toLowerCase();
  if (!code) return false;
  return EXPECTED_BACKEND_CODES.some((expected) => code.includes(expected));
}

function errorText(error: unknown): string {
  if (error instanceof Error) return `${error.name} ${error.message}`;
  const message = (error as { message?: unknown } | null)?.message;
  if (typeof message === 'string' && message.trim()) return message;
  return typeof error === 'string' ? error : String(error ?? '');
}

export function isTransientOpsNetworkError(text: string): boolean {
  const msg = text.toLowerCase();
  return (
    msg.includes('networkerror') ||
    msg.includes('a network error occurred') ||
    msg.includes('failed to fetch') ||
    // RN Android / OkHttp: "fetch failed: java.net.UnknownHostException: ..."
    msg.includes('fetch failed') ||
    msg.includes('unknownhostexception') ||
    msg.includes('unable to resolve host') ||
    // Mensaje estándar de fetch en React Native.
    msg.includes('network request failed') ||
    msg.includes('failed to send a request') ||
    msg.includes('err_network') ||
    msg.includes('load failed') ||
    msg.includes('timeout') ||
    msg.includes('timed out') ||
    msg.includes('connection refused') ||
    msg.includes('connection reset') ||
    msg.includes('aborted')
  );
}

/**
 * Errores esperados de negocio/dominio: validación que el backend rechazó
 * correctamente, permisos, o dominio tipado (`*ValidationError`, stock).
 * El usuario ya ve el toast friendly — reportar a Sentry es ruido.
 * NO incluye red transitoria ni auth (cada path decide eso aparte).
 */
export function isExpectedBusinessError(error: unknown, ingestError?: string | null): boolean {
  if (error instanceof Error) {
    if (EXPECTED_ERROR_NAMES.has(error.name)) return true;
    if (error.name.endsWith('ValidationError')) return true;
  }
  // Defensa ante minificación: `StockInsufficientError` puede llegar como `s`.
  const maybeCode = (error as { code?: unknown } | null)?.code;
  if (typeof maybeCode === 'string') {
    if (EXPECTED_STOCK_DOMAIN_CODES.has(maybeCode)) return true;
    if (matchesExpectedBackendCode(maybeCode)) return true;
  }
  const maybeBackendCode = (error as { backendCode?: unknown } | null)?.backendCode;
  if (typeof maybeBackendCode === 'string' && matchesExpectedBackendCode(maybeBackendCode)) {
    return true;
  }
  // PostgrestError plano o repo que tira el mensaje crudo sin mapear.
  if (matchesExpectedBackendCode(errorText(error))) return true;

  const ingest = ingestError?.trim().toLowerCase();
  if (ingest) {
    for (const code of EXPECTED_INGEST_CODES) {
      if (ingest.startsWith(code)) return true;
    }
    // saleIntake a veces re-captura con el mensaje friendly (sin código backend).
    if (ingest.includes('no hay stock suficiente')) return true;
  }
  return errorText(error).toLowerCase().includes('no hay stock suficiente');
}

/**
 * Sesión expirada / JWT inválido durante una op: el auth layer maneja
 * refresh+logout y el write queda encolado para reintento. No es bug del módulo
 * (VUELTO-NEGOCIO-3E). Mismos patrones que `isAuthErrorMessage`, inline porque
 * este archivo es hoja sin imports.
 */
function isExpectedAuthError(text: string): boolean {
  return (
    text.includes('jwt expired') ||
    text.includes('invalid jwt') ||
    text.includes('pgrst303') ||
    text.includes('auth session missing') ||
    text.includes('not authenticated') ||
    text.includes('session required') ||
    text.includes('sesión requerida') ||
    text.includes('sesion requerida') ||
    text.includes('permission denied for function get_mobile_catalog_snapshot')
  );
}

/**
 * Errores esperados en writes operativos: negocio + red transitoria + sesión
 * expirada (el sync coordinator reintenta). Versión completa para `captureOpsFailure`.
 */
export function isExpectedOpsError(error: unknown, ingestError?: string | null): boolean {
  if (isExpectedBusinessError(error, ingestError)) return true;
  const text = [errorText(error), ingestError ?? ''].join(' ').toLowerCase();
  if (isTransientOpsNetworkError(text)) return true;
  return isExpectedAuthError(text);
}
